/**
 * Warranty report export: CSV and PDF with disclaimer.
 * Authority: docs/implementation/BATTERY_HEALTH_TRACKING.md
 * Confidence: Measured data (cycles_counted, computed capacity) is high; estimated historical cycles use LiFePO4 curve — not exact for warranty claims.
 */
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import type { WarrantyThresholds } from "./battery-degradation";

/** Shown on PDF and included at top of CSV. Installer must not treat estimates as exact for warranty claims. */
export const WARRANTY_REPORT_DISCLAIMER =
  "This report is for documentation support only. Measured values (computed capacity, cycles since monitoring start) are from system telemetry. Historical cycle estimates use a generic LiFePO4 curve and are not exact. Do not rely solely on this report for warranty claims; verify with manufacturer or installer records.";

export interface WarrantyReportSnapshot {
  snapshot_date: string;
  configured_capacity_kwh: number;
  computed_capacity_kwh: number | null;
  usable_capacity_kwh: number | null;
  total_degradation_percent: number | null;
  cycles_counted: number;
  estimated_historical_cycles: number | null;
  estimated_total_cycles: number | null;
  soc_min: number | null;
  soc_max: number | null;
  charge_kwh: number | null;
  discharge_kwh: number | null;
}

export interface WarrantyReportInput {
  plantDisplayName: string;
  snapshot: WarrantyReportSnapshot | null;
  trendSnapshots: WarrantyReportSnapshot[];
  warranty: WarrantyThresholds | null;
  healthScore?: { score: number; grade: string; status: string; summary: string } | null;
  generatedAt?: string;
}

const CSV_HEADERS = [
  "Date",
  "Configured (kWh)",
  "Computed (kWh)",
  "Usable (kWh)",
  "Degradation (%)",
  "Cycles (counted)",
  "Est. total cycles",
  "SOC min",
  "SOC max",
  "Charge (kWh)",
  "Discharge (kWh)",
];

function escapeCsvCell(value: string | number | null | undefined): string {
  if (value == null) return "";
  const s = String(value);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

/** Build full CSV content including disclaimer and data rows. */
export function buildWarrantyReportCsv(input: WarrantyReportInput): string {
  const { plantDisplayName, trendSnapshots, warranty, generatedAt } = input;
  const lines: string[] = [];
  lines.push(`Battery Health / Warranty Data,${escapeCsvCell(plantDisplayName)}`);
  if (generatedAt) lines.push(`Generated,${escapeCsvCell(generatedAt)}`);
  if (warranty) {
    lines.push(`Warranty terms,${warranty.warrantyYears} years, ${warranty.warrantyCycles} cycles, ${warranty.warrantyCapacityPercent}% capacity`);
  }
  lines.push("");
  lines.push(`Disclaimer,${escapeCsvCell(WARRANTY_REPORT_DISCLAIMER)}`);
  lines.push("");
  lines.push(CSV_HEADERS.map(escapeCsvCell).join(","));

  for (const s of trendSnapshots) {
    lines.push(
      [
        s.snapshot_date,
        s.configured_capacity_kwh,
        s.computed_capacity_kwh ?? "",
        s.usable_capacity_kwh ?? "",
        s.total_degradation_percent ?? "",
        s.cycles_counted,
        s.estimated_total_cycles ?? "",
        s.soc_min ?? "",
        s.soc_max ?? "",
        s.charge_kwh ?? "",
        s.discharge_kwh ?? "",
      ].map(escapeCsvCell).join(",")
    );
  }

  return lines.join("\r\n");
}

/** Trigger browser download of warranty report as CSV. */
export function downloadWarrantyReportCsv(input: WarrantyReportInput, filename?: string): void {
  const csv = buildWarrantyReportCsv(input);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename ?? `warranty-report-${input.plantDisplayName.replace(/\s+/g, "-").slice(0, 40)}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

/** Build and download warranty report as PDF (jspdf + autotable). */
export function downloadWarrantyReportPdf(input: WarrantyReportInput, filename?: string): void {
  const { plantDisplayName, snapshot, trendSnapshots, warranty, healthScore, generatedAt } = input;
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = doc.getPageWidth();
  let y = 14;

  doc.setFontSize(14);
  doc.text("Battery Health / Warranty Report", 14, y);
  y += 8;

  doc.setFontSize(10);
  doc.text(plantDisplayName, 14, y);
  y += 6;
  if (generatedAt) {
    doc.text(`Generated: ${generatedAt}`, 14, y);
    y += 6;
  }

  if (warranty) {
    doc.text(
      `Warranty terms: ${warranty.warrantyYears} years, ${warranty.warrantyCycles} cycles, ${warranty.warrantyCapacityPercent}% capacity threshold.`,
      14,
      y
    );
    y += 6;
  }

  if (healthScore) {
    doc.text(
      `Health score: ${healthScore.score}/100 (${healthScore.grade}) — ${healthScore.status}. ${healthScore.summary}`,
      14,
      y
    );
    y += 8;
  }

  if (snapshot) {
    doc.setFontSize(11);
    doc.text("Current snapshot", 14, y);
    y += 6;
    doc.setFontSize(10);
    autoTable(doc, {
      startY: y,
      head: [["Metric", "Value"]],
      body: [
        ["Configured capacity (kWh)", String(snapshot.configured_capacity_kwh?.toFixed(1) ?? "—")],
        ["Computed capacity (kWh)", snapshot.computed_capacity_kwh != null ? snapshot.computed_capacity_kwh.toFixed(1) : "—"],
        ["Usable capacity (kWh)", snapshot.usable_capacity_kwh != null ? snapshot.usable_capacity_kwh.toFixed(1) : "—"],
        ["Degradation (%)", snapshot.total_degradation_percent != null ? snapshot.total_degradation_percent.toFixed(1) : "—"],
        ["Cycles (counted)", String(snapshot.cycles_counted ?? 0)],
        ["Est. total cycles", snapshot.estimated_total_cycles != null ? String(Math.round(snapshot.estimated_total_cycles)) : "—"],
      ],
      margin: { left: 14 },
      theme: "plain",
      styles: { fontSize: 9 },
    });
    y = (doc as jsPDF & { lastAutoTable?: { finalY?: number } }).lastAutoTable?.finalY ?? y + 30;
    y += 8;
  }

  if (trendSnapshots.length > 0) {
    doc.setFontSize(11);
    doc.text("Historical snapshots (recent)", 14, y);
    y += 6;
    const rows = trendSnapshots.slice(-30).map((s) => [
      s.snapshot_date,
      s.configured_capacity_kwh.toFixed(1),
      s.computed_capacity_kwh != null ? s.computed_capacity_kwh.toFixed(1) : "—",
      s.total_degradation_percent != null ? s.total_degradation_percent.toFixed(1) : "—",
      String(s.cycles_counted),
    ]);
    autoTable(doc, {
      startY: y,
      head: [["Date", "Configured (kWh)", "Computed (kWh)", "Degradation (%)", "Cycles"]],
      body: rows,
      margin: { left: 14 },
      theme: "striped",
      styles: { fontSize: 8 },
    });
    y = (doc as jsPDF & { lastAutoTable?: { finalY?: number } }).lastAutoTable?.finalY ?? y + 40;
    y += 8;
  }

  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  const disclaimerLines = doc.splitTextToSize(WARRANTY_REPORT_DISCLAIMER, pageW - 28);
  if (y + disclaimerLines.length * 4 > doc.getPageHeight() - 20) {
    doc.addPage();
    y = 14;
  }
  doc.text(disclaimerLines, 14, y);
  doc.setTextColor(0, 0, 0);

  doc.save(filename ?? `warranty-report-${plantDisplayName.replace(/\s+/g, "-").slice(0, 30)}-${new Date().toISOString().slice(0, 10)}.pdf`);
}
